import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import {
  selectExcludedProjectsIds,
  selectSelectedProjectsIds,
  selectCanExportProjects,
} from 'state/ProjectsList/export/exportSlice';
import { selectFilterParams } from 'state/ProjectsList/filters/filtersSlice';
import { exportProjects } from 'api/exportProjects';
import {
  extractFilenameFromContentDisposition,
  createDownloadLinkAndDownloadFile,
} from 'utils/csvExports';
import { buildApiUrl } from './helpers';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import ExportButton from 'Organisms/TableHeaderActionButtons/ExportButton';

const ExportProjectsButton = () => {
  const [isExporting, setIsExporting] = useState(false);
  const selectedProjectsIds = useSelector(selectSelectedProjectsIds);
  const excludedProjectsIds = useSelector(selectExcludedProjectsIds);
  const filterParams = useSelector(selectFilterParams);
  const canExportProjects = useSelector(selectCanExportProjects);

  const {
    SnackbarNotification,
    isNotificationVisible,
    showNotification,
    snackbarProps,
  } = useSnackbarNotification();

  const handleExport = useCallback(async () => {
    const url = buildApiUrl({
      quickFilters: filterParams.quickFilters,
      filter: filterParams.filter,
      search: filterParams.search,
      export: {
        selectedProjectsIds,
        excludedProjectsIds,
      },
    });
    setIsExporting(true);
    try {
      const response = await exportProjects({ url });
      const filename = extractFilenameFromContentDisposition(
        response.headers['content-disposition']
      );
      const blob = new Blob([response.data], { type: 'text/csv' });
      createDownloadLinkAndDownloadFile(blob, filename);
    } catch (error) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get('ERROR_SNACKBAR_TITLE'),
        notificationMessage: intl.get('ERROR_SNACKBAR_MESSAGE'),
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    filterParams.quickFilters,
    filterParams.filter,
    filterParams.search,
    selectedProjectsIds,
    excludedProjectsIds,
    showNotification,
  ]);

  return (
    <>
      <ExportButton
        onExport={handleExport}
        exportTooltipText=''
        disabled={!canExportProjects}
        loading={isExporting}
        aria-label={isExporting ? intl.get('LOADING') : intl.get('EXPORT')}
      />
      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}
    </>
  );
};

export default ExportProjectsButton;
